import { Dispatch, SetStateAction } from 'react';

import { OneClickCorrection } from '../Components/OneClickPad/OneClickPadOld';
import { CorrectionBlock, LLMError } from "../Hooks/useOneClickStore";

export const findPositionOfFirstDifference = (oldString: string, newString: string): number | null => {
    const minLength = Math.min(oldString.length, newString.length);

    for (let i = 0; i < minLength; i++) {
        if (oldString[i] !== newString[i]) {
            return i;
        }
    }

    if (oldString.length !== newString.length) {
        return minLength;
    }

    return null;
};

export const recalculateOffsets = (
    setCorrectionMarkers: Dispatch<SetStateAction<OneClickCorrection[]>>,
    stringDiffPosition: number,
    stringDiffLength: number
) =>
    setCorrectionMarkers((corrections: OneClickCorrection[]) => {
        return corrections.map((correction) => {
            const correctionOffsetStart = correction.offset;
            const correctionOffsetEnd = correction.offset + correction.length;

            if (stringDiffPosition > correctionOffsetStart && stringDiffPosition <= correctionOffsetEnd) {
                // Remove the marker in which the change was made
                return false;
            }
            let additionalOffset = 0;

            if (stringDiffPosition <= correctionOffsetStart) {
                additionalOffset += stringDiffLength;
            }
            return { ...correction, offset: correctionOffsetStart + additionalOffset };
        }).filter(Boolean);
    });


export const getAugmentedTextNodes = (elem: HTMLElement, apiText: string): NodeItem[] => {
    const treeWalker = document.createTreeWalker(elem, NodeFilter.SHOW_TEXT);
    let currentNode: Node | null = treeWalker.currentNode;
    const nodes = [];
    let lastPosition = 0;
    let i = 0;

    // Iterates over text nodes and builds helper array.
    while (currentNode) {
        currentNode = treeWalker.nextNode();
        if (currentNode) {
            if (!currentNode.textContent) {
                continue;
            }
            const textPositions = indexOfIgnoreSpace(apiText, currentNode.textContent, lastPosition);
            if (textPositions === null) {
                continue;
            }

            nodes.push({
                index: i++,
                node: currentNode,
                start: textPositions.begin,
                end: textPositions.end,
                length: textPositions.end - textPositions.begin,
            });

            lastPosition = textPositions.end;
        }
    }
    return nodes;
};

export const indexOfIgnoreSpace = (
    text: string,
    searchValue: string,
    fromIndex: number
): { begin: number; end: number; ignoredSpaces: { [key: number]: number } } | null => {
    let begin = 0;
    let end = -1;
    const spaceRegex = /[\p{Z}\p{Cc}]+/gu;
    const fragment = searchValue.slice(0).split(spaceRegex).join(""); // remove whitespace
    const ignoredSpaces: { [key: number]: number } = {};
    const spaceMatch = Array.from(searchValue.matchAll(spaceRegex));

    spaceMatch.forEach((match) => {
        if (match.index !== undefined) {
            ignoredSpaces[match.index] = match[0].length;
        }
    });

    if (!fromIndex) {
        fromIndex = 0;
    }

    for (let i = fromIndex; i < text.length; i++) {
        // do not start matching on whitespace
        if (text[i].match(spaceRegex)) {
            continue;
        }

        let index = 0;
        begin = i;

        for (let j = i; j < text.length; j++) {
            if (text[j].match(spaceRegex)) {
                continue;
            }

            if (text[j] !== fragment[index]) {
                break;
            }

            index++;
            if (index === fragment.length) {
                end = j + 1;
                break;
            }
        }

        if (end !== -1) {
            return {
                begin: begin,
                end: end,
                ignoredSpaces: ignoredSpaces,
            };
        }
    }

    return null;
};

export const getAffectedNodes = (nodeTree: NodeItem[], markerStart: number, markerEnd: number): NodeItem[] => {
    return nodeTree.filter((node) => {
        return (
            (node.start <= markerStart && node.end > markerStart) ||
            (node.start > markerStart && node.end < markerEnd) ||
            (node.start < markerEnd && node.end >= markerEnd)
        );
    });
};
export const getTextNodeBoundaries = (
    shortText: string,
    start: number,
    end: number,
    currentNode: NodeItem,
    affectedNodes: NodeItem[]
): [number, number] => {
    const firstNode = affectedNodes[0];
    const lastNode = affectedNodes[affectedNodes.length - 1];
    let rangeStart = 0;
    let rangeEnd = currentNode.length;

    let isCenterNode = true;
    if (currentNode.index === firstNode.index) {
        rangeStart = start - firstNode.start;
        isCenterNode = false;
    }
    // Is end node
    if (currentNode.index === lastNode.index) {
        rangeEnd = end - lastNode.start;
        isCenterNode = false;
    }

    // special case "center node" is always the full length of the node
    if (isCenterNode) {
        return [0, currentNode.node.textContent?.length ?? rangeEnd];
    }

    return [rangeStart, rangeEnd];
};

export const getText = (editorRef: HTMLElement) => {
    const innerText = editorRef.innerText || "";
    // innerText includes non-breaking spaces, DPF does not like them, so we replace all spaces with actual plain spaces
    const originalText = innerText.replace(/\p{Z}/gu, " ");
    const paragraphs: string[] = originalText.split(/\r?\n|\r|\n/g);
    const paragraphsWithOffets = [];
    let offset = 0;
    for (let i = 0; i < paragraphs.length; i++) {
        paragraphsWithOffets.push({ additionalOffset: offset, paragraphText: paragraphs[i] });
        offset += paragraphs[i].length + 1;
    }

    // extend by paragraph length / additional offsets for each paragraph (additionalOffset = sum(previousParagraphs.length))
    return { originalText, paragraphs: paragraphsWithOffets };
};

export const normalizeCorrectionBlocks = (text: string, errors?: LLMError[], options?: { showTraces: boolean }) => {
    const { showTraces } = options ?? {};
    const blocks: CorrectionBlock[] = [];
    let sinitizedText = text;

    const sortedErrors = errors?.sort((a, b) => b.offset - a.offset || a.errorcode.localeCompare(b.errorcode)) ?? [];

    if (sortedErrors?.length) {
        sortedErrors.forEach((error) => {
            if (showTraces && error.errorcode === 'remove') {
                return;
            }

            if (!showTraces && error.errorcode === 'trace') {
                return;
            }

            const commonCopy = sinitizedText?.substring(error.offset + error.length);
            const markCopy = error.errorcode === 'trace' ? error.proposals[0] : sinitizedText?.substring(error.offset, error.offset + error.length);

            if (!markCopy) return;

            commonCopy && blocks.unshift({
                type: 'common',
                copy: commonCopy
            })

            blocks.unshift({
                type: error.errorcode,
                copy: markCopy
            })

            sinitizedText = sinitizedText?.substring(0, error.offset);
        });

        sinitizedText && blocks.unshift({
            type: 'common',
            copy: sinitizedText
        })
    } else {
        blocks.push({
            type: 'common',
            copy: sinitizedText,
        });
    }

    return blocks;
}